import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useLanguage } from "@/context/LanguageContext";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { SiteFooter } from "@/components/shared/SiteFooter";
import { getBackendBaseUrl } from "@/lib/config";
import { getCachedPricingSnapshot, prefetchPricingSnapshot } from "@/lib/pricingPrefetch";
import { Check, X, ArrowLeft } from "lucide-react";
import axios from "axios";

const API_URL = getBackendBaseUrl();

const featureMap = {
  starter: ["tenHoursDay", "fullTranscription", "keyMomentDetection", "aiSummary", "exportTxtSrt", "standardSupport", "priorityProcessing", "teamUse"],
  professional: ["fifteenHoursDay", "fullTranscription", "keyMomentDetection", "aiSummary", "exportAll", "priorityProcessing", "detailedSummaries", "prioritySupport"],
  enterprise: ["unlimitedHours", "fullTranscription", "keyMomentDetection", "detailedSummaries", "allExportFormats", "teamUse", "adminControls", "premiumSupport"],
};

export default function PricingPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t, language } = useLanguage();
  const pricingViewerKey = user?.id || "guest";
  const cachedPricingSnapshot = getCachedPricingSnapshot(pricingViewerKey);

  const [plans, setPlans] = useState(cachedPricingSnapshot?.plans || []);
  const [subscription, setSubscription] = useState(cachedPricingSnapshot?.subscription || null);
  const [providers, setProviders] = useState(cachedPricingSnapshot?.providers || null);


  useEffect(() => {
    const loadData = async () => {
      try {
        const snapshot = await prefetchPricingSnapshot({
          includeSubscription: Boolean(user),
          force: !cachedPricingSnapshot,
          viewerKey: pricingViewerKey,
        });
        setPlans(snapshot?.plans || []);
        setProviders(snapshot?.providers || null);
        setSubscription(snapshot?.subscription || null);
      } catch (error) {
        console.error("Error loading pricing data:", error);

        try {
          const [plansResponse, providersResponse] = await Promise.all([
            axios.get(`${API_URL}/api/subscription/plans`),
            axios.get(`${API_URL}/api/subscription/providers`),
          ]);
          setPlans(plansResponse.data.plans || []);
          setProviders(providersResponse.data);

          if (user) {
            const response = await axios.get(`${API_URL}/api/subscription/status`);
            setSubscription(response.data);
          }
        } catch (fallbackError) {
          console.error("Fallback pricing load failed:", fallbackError);
        }
      }
    };

    loadData();
  }, [cachedPricingSnapshot, pricingViewerKey, user]);

  const currentPlan = subscription?.plan_id || null;
  const mercadoPagoCountryList = providers?.mercadopago?.countries || [];
  const detectedMercadoPagoCountry = (mercadoPagoCountryList.some((country) => country.code === providers?.detected_country_code) ? providers.detected_country_code : null) || providers?.mercadopago_default_country_code || "CO";
  const detectedMercadoPagoCountryName = mercadoPagoCountryList.find((country) => country.code === detectedMercadoPagoCountry)?.name || "Colombia";
  const currentPrice = useMemo(() => {
    if (!currentPlan) return 0;
    return plans.find((plan) => plan.id === currentPlan)?.price || 0;
  }, [currentPlan, plans]);

  const getActionType = (plan) => {
    if (!user) return "signup";
    if (currentPlan === plan.id) return "current";
    if (subscription?.has_subscription && currentPlan !== "free") return Number(plan.price) > Number(currentPrice) ? "manage-upgrade" : "manage-change";
    return "new";
  };

  const planCards = plans.map((plan) => ({
    ...plan,
    featured: plan.id === "professional",
    perUser: plan.id === "enterprise",
    displayPrice: Number(plan.price).toFixed(2),
    features: (featureMap[plan.id] || []).map((featureKey, index) => ({
      text: t(featureKey),
      included: !(plan.id === "starter" && [6, 7].includes(index)),
    })),
  }));

  return (
    <div className="min-h-screen bg-background" data-testid="pricing-page">
      <header className="fixed top-0 left-0 right-0 z-50 glass" data-testid="pricing-header">
        <div className="container mx-auto px-6 py-4 flex items-center justify-between">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate(user ? "/dashboard" : "/")}
            className="text-muted-foreground hover:text-foreground"
            data-testid="back-button"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            {language === "es" ? "Volver" : "Back"}
          </Button>

          <div className="flex items-center gap-3">
            {user && (
              <Button variant="outline" onClick={() => navigate("/billing")} data-testid="pricing-manage-billing-button">
                {language === "es" ? "Administrar suscripción" : "Manage subscription"}
              </Button>
            )}
            <Link to="/" className="flex items-center" data-testid="logo-link">
              <div className="h-12 w-12 overflow-hidden">
                <img
                  src="/assets/voxara-logo-optimized.webp"
                  alt="Voxara"
                  className="h-full w-auto max-w-none"
                  width="120"
                  height="80"
                  loading="eager"
                  decoding="sync"
                  style={{ transform: "scale(3.5)", transformOrigin: "28% center" }}
                />
              </div>
            </Link>
          </div>
        </div>
      </header>

      <main className="pt-24 pb-12 px-6">
        <div className="mb-12 text-center">
          <h2 className="text-4xl md:text-5xl font-bold">
            <span className="text-foreground">{language === "es" ? "Precios" : "Simple,"}</span>{" "}
            <span className="gradient-text italic">{language === "es" ? "simples y honestos" : "honest pricing"}</span>
          </h2>
          <p className="mt-4 text-sm text-muted-foreground" data-testid="pricing-provider-note">
            {language === "es"
              ? `${detectedMercadoPagoCountryName} usa Mercado Pago para cobrar en tu moneda local. Ese es el valor que se muestra y se cobra.`
              : `${detectedMercadoPagoCountryName} uses Mercado Pago to charge in your local currency. That is the value shown and charged.`}
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
          {planCards.map((plan) => {
            const isCurrentPlan = currentPlan === plan.id;
            const actionType = getActionType(plan);
            const localQuote = providers?.mercadopago?.quotes?.[detectedMercadoPagoCountry]?.[plan.id];

            return (
              <Card
                key={plan.id}
                className={`p-6 relative ${
                  plan.featured
                    ? "bg-card border-primary shadow-[0_0_30px_rgba(248,0,248,0.2)]"
                    : isCurrentPlan
                    ? "bg-card border-green-500"
                    : "bg-card border-border"
                }`}
                data-testid={`plan-card-${plan.id}`}
              >
                {plan.featured && !isCurrentPlan && (
                  <span className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 text-xs font-mono bg-primary text-primary-foreground rounded-full">
                    {t("mostPopular")}
                  </span>
                )}
                {isCurrentPlan && (
                  <span className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 text-xs font-mono bg-green-500 text-white rounded-full">
                    {language === "es" ? "Plan Actual" : "Current Plan"}
                  </span>
                )}
                <div className="text-sm font-mono text-muted-foreground uppercase tracking-wider mb-4">{plan.name}</div>
                <div className="mb-6">
                  <div className="flex items-baseline gap-1">
                    <span className="text-sm text-muted-foreground">$</span>
                    <span className="text-5xl font-bold">{plan.displayPrice}</span>
                  </div>
                  {localQuote && (
                    <p className="mt-2 text-xs text-primary" data-testid={`plan-local-quote-${plan.id}`}>
                      {localQuote.currency} {Number(localQuote.amount).toLocaleString(language === "es" ? "es-CO" : "en-US")}
                    </p>
                  )}
                </div>
                <div className="text-xs text-muted-foreground mb-6">
                  {plan.perUser ? (language === "es" ? "por usuario/mes" : "per user/month") : t("perMonth")}
                </div>
                <div className="border-t border-border pt-6 mb-6">
                  <ul className="space-y-3">
                    {plan.features.map((feature, index) => (
                      <li key={`${plan.id}-${index}`} className={`flex items-center gap-2 text-sm ${feature.included ? "text-foreground" : "text-muted-foreground/50"}`}>
                        {feature.included ? <Check className="w-4 h-4 text-green-500" /> : <X className="w-4 h-4" />}
                        {feature.text}
                      </li>
                    ))}
                  </ul>
                </div>

                {actionType === "signup" && (
                  <Link to="/signup">
                    <Button className="w-full" data-testid={`select-plan-${plan.id}`}>{plan.id === "professional" ? t("startFreeTrial") : t("getStarted")}</Button>
                  </Link>
                )}

                {actionType === "current" && (
                  <Button className="w-full bg-green-600 text-white cursor-default" disabled data-testid={`select-plan-${plan.id}`}>
                    {language === "es" ? "Plan Actual" : "Current Plan"}
                  </Button>
                )}

                {actionType === "new" && (
                  <div className="space-y-3">
                    {localQuote && (
                      <div className="rounded-xl border border-border/70 bg-muted/20 px-3 py-2 text-xs text-muted-foreground">
                        {language === "es"
                          ? `Base USD ${Number(plan.price).toFixed(2)} / ${detectedMercadoPagoCountryName}: ${localQuote.currency} ${Number(localQuote.amount).toLocaleString("es-CO")}`
                          : `Base USD ${Number(plan.price).toFixed(2)} / ${detectedMercadoPagoCountryName}: ${localQuote.currency} ${Number(localQuote.amount).toLocaleString("en-US")}`}
                      </div>
                    )}
                    <Button
                      variant="outline"
                      className="w-full"
                      data-testid={`select-plan-${plan.id}-mercadopago`}
                      onClick={() => navigate(`/billing?plan=${plan.id}&provider=mercadopago&country=${detectedMercadoPagoCountry}`)}
                    >
                      {language === "es" ? "Seleccionar" : "Select"}
                    </Button>
                  </div>
                )}

                {(actionType === "manage-upgrade" || actionType === "manage-change") && (
                  <Button
                    className="w-full"
                    variant="outline"
                    data-testid={`select-plan-${plan.id}-manage`}
                    onClick={() => navigate(`/billing?plan=${plan.id}`)}
                  >
                    {actionType === "manage-upgrade"
                      ? (language === "es" ? "Administrar upgrade" : "Manage upgrade")
                      : (language === "es" ? "Administrar cambio" : "Manage change")}
                  </Button>
                )}
              </Card>
            );
          })}
        </div>
      </main>

      <SiteFooter />
    </div>
  );
}
